/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useFetch from 'use-http';
import { useHistory } from 'react-router-dom';
import { VisitServiceDetails } from './VisitServiceDetails';
import { VisitGeneralDetails } from './VisitGeneralDetails';
import { VisitAppointmentServiceRequestDetails } from './VisitAppointmentServiceRequestDetails';
import { VisitE025Details } from './VisitE025Details';
import { VisitPrescriptionsDetails } from './VisitPrescriptionsDetails';
import { VisitReferralsDetails } from './VisitReferralsDetails';
import { VisitVaccinationDetails } from './VisitVaccinationDetails';
import { VisitLabaratoryResearchDetails } from './VisitLabaratoryResearchDetails';
import Page from '@/components/shared/Page';
import Loader from '@/components/shared/Loader';
import { useUserState } from '@/components/context/UserContext';
import InstrumentalResearchDocumentsCard from '../../UserAccountInstrumentalResearchDetailsPage/Components/InstrumentalResearchDocumentsCard';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { SlashIcon } from 'lucide-react';
import { Link } from 'react-router-dom/cjs/react-router-dom';
import { useParams } from 'react-router-dom/cjs/react-router-dom.min';
import { getQueryString } from '@/components/constants/visitsQueryStringBuilder';

const VisitDetails = (props) => {
  const history = useHistory();
  const vaccinationRef = useRef(null);
  const referralsRef = useRef(null);
  const instrumentalResearchesRef = useRef(null);
  const laboratoryResearchesRef = useRef(null);
  const prescriptionsRef = useRef(null);
  const e025Ref = useRef(null);

  const [visitDetails, setVisitDetails] = useState(undefined);
  const [visit, setVisit] = useState(history.location.state?.visit || null);
  const [loading, setLoading] = useState(false);

  const { t } = useTranslation();
  const userState = useUserState();
  const { id } = useParams();

  const executeScroll = (ref) => scrollToRef(ref);
  const scrollToRef = (ref) => {
    window.scrollTo(0, ref.current.offsetTop + 145);
  };

  const initialVisit = history.location.state?.visit || null;

  const { get: getVisitDetails } = useFetch('/UserAccount/VisitDetails');
  const { get: getVisit } = useFetch('/UserAccount/Visits');
  const { get: getFile } = useFetch('/UserAccount/DownloadAmisFile');

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      const visitIdToLoad = id || initialVisit?.visitId;

      if (!visitIdToLoad) return;

      setLoading(true);

      try {
        const requests = [getUserVisitDetailsInformation(visitIdToLoad)];

        if (id) {
          requests.push(getBaseVisit(id));
        }

        const [detailsResult, baseVisitResult] = await Promise.all(requests);

        if (!isMounted) return;

        if (detailsResult) {
          setVisitDetails(detailsResult);
        }

        if (id && baseVisitResult) {
          setVisit(baseVisitResult);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [id, initialVisit?.visitId, userState.espbiEsi]);

  const getBaseVisit = async (visitId) => {
    const query = getQueryString(
      undefined,
      '',
      'history',
      undefined,
      userState.espbiEsi,
    );

    const response = await getVisit(query + `&id=${visitId}`);
    return response?.[0] || null;
  };

  const getUserVisitDetailsInformation = async (visitId) => {
    const queryString = userState.espbiEsi
      ? `?visitId=${visitId}&espbiEsi=${userState.espbiEsi}`
      : `?visitId=${visitId}`;

    const res = await getVisitDetails(queryString);
    return res;
  };

  const handleFileDownload = async (category, filename, fileType, fileGuid) => {
    await getFile(
      `?fileType=${fileType}&fileGuid=${fileGuid}&category=${category}&espbiEsi=${userState.espbiEsi}`,
    ).then((res) => {
      const base64Data = res;
      const blob = base64toBlob(base64Data, 'application/pdf');
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `${filename}.pdf`;
      document.body.appendChild(downloadLink);
      setTimeout(() => {
        downloadLink.click();
        document.body.removeChild(downloadLink);
      }, 120);
    });
  };

  function base64toBlob(base64Data, contentType) {
    contentType = contentType || '';
    const sliceSize = 1024;
    const byteCharacters = atob(base64Data);
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }

    return new Blob(byteArrays, { type: contentType });
  }

  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1);
  };

  useEffect(() => {
    scrollToTitle(props);
  }, [props]);

  function scrollToTitle(props) {
    switch (props.scrollTo) {
      case 'prescriptionsRef':
        executeScroll(prescriptionsRef);
        break;
      case 'vaccinationRef':
        executeScroll(vaccinationRef);
        break;
      case 'instrumentalResearchesRef':
        executeScroll(instrumentalResearchesRef);
        break;
      case 'e025Ref':
        executeScroll(e025Ref);
        break;
      case 'referralsRef':
        executeScroll(referralsRef);
        break;
      default:
        break;
    }
  }

  if (loading) {
    return (
      <Page>
        <Loader />
      </Page>
    );
  }

  return (
    <>
      <Breadcrumb className="px-4 mb-2">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/userAccount/visits">Mano vizitai</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator>
            <SlashIcon />
          </BreadcrumbSeparator>
          <BreadcrumbItem>
            <BreadcrumbPage>
              {capitalizeFirstLetter(visit?.visitTitle)}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {Array.isArray(visit?.services) && visit?.services.length > 0 ? (
        <VisitServiceDetails visitDetails={visit} />
      ) : (
        <VisitGeneralDetails visitDetails={visit} />
      )}

      {visitDetails?.appointmentServiceRequest && (
        <div className="mb-4">
          <VisitAppointmentServiceRequestDetails
            appointmentServiceRequest={visitDetails.appointmentServiceRequest}
            handleFileDownload={handleFileDownload}
          />
        </div>
      )}

      {Array.isArray(visitDetails?.e025) && visitDetails.e025.length > 0 && (
        <VisitE025Details
          e025List={visitDetails.e025}
          e025Ref={e025Ref}
          handleFileDownload={handleFileDownload}
        />
      )}

      {Array.isArray(visitDetails?.medicationStatements) &&
        visitDetails.medicationStatements.length > 0 && (
          <VisitPrescriptionsDetails
            prescriptions={visitDetails.medicationStatements}
            prescriptionsRef={prescriptionsRef}
            handleFileDownload={handleFileDownload}
          />
        )}

      {Array.isArray(visitDetails?.serviceRequests) &&
        visitDetails.serviceRequests.length > 0 && (
          <div ref={referralsRef}>
            <VisitReferralsDetails
              referrals={visitDetails.serviceRequests}
              referralsRef={referralsRef}
              handleFileDownload={handleFileDownload}
            />
          </div>
        )}

      {Array.isArray(visitDetails?.instrumentalDiagnosticReports) &&
        visitDetails.instrumentalDiagnosticReports.length > 0 && (
          <div ref={instrumentalResearchesRef} className="mt-5 mb-5 space-y-5">
            {visitDetails.instrumentalDiagnosticReports.map(
              (research, index) => (
                <InstrumentalResearchDocumentsCard
                  key={research?.id ?? research?.Id ?? index}
                  research={research}
                  component="visits"
                />
              ),
            )}
          </div>
        )}

      {Array.isArray(visitDetails?.immunizations) &&
        visitDetails.immunizations.length > 0 && (
          <VisitVaccinationDetails
            vaccinations={visitDetails.immunizations}
            vaccinationRef={vaccinationRef}
            handleFileDownload={handleFileDownload}
          />
        )}

      {Array.isArray(visitDetails?.laboratoryFiles) &&
        visitDetails.laboratoryFiles.length > 0 && (
          <VisitLabaratoryResearchDetails
            laboratoryFiles={visitDetails.laboratoryFiles}
            laboratoryResearchesRef={laboratoryResearchesRef}
            handleFileDownload={handleFileDownload}
          />
        )}
    </>
  );
};

export default VisitDetails;
